'use client'

/**
 * L'**ace**: la palla arriva dal campo di chi ha battuto, batte sul pavimento
 * avversario e **scoppia** lì — onda d'urto, polvere di fondo campo, schegge —
 * mentre «ACE» pulsa tre volte e la scena si chiude a 2,4 s.
 *
 * PERCHÉ NON UNA PALLA CHE ESPLODE E BASTA: l'ace non è la palla che scoppia in
 * volo, è la palla che **cade e nessuno tocca**. Lo scoppio sta dove la palla
 * tocca terra: è il pavimento che risponde, non la palla che si rompe. Per
 * questo la palla resta intera fino all'impatto e sparisce dentro lo scoppio.
 *
 * Procedurale come il muro: posizioni, angoli e distanze delle schegge sono
 * calcolati qui, senza `random` (stesso ace = stessa esplosione), e tutte le
 * misure sono in `cqi`, così la scena regge da 190 a 672 px di card.
 *
 * **DUE METÀ, DUE RUOLI**: il pallone batte nel campo di chi subisce l'ace, la
 * scritta compare nel campo di chi lo fa. Per questo il componente si monta due
 * volte, con `variant`:
 *
 * - `landing` → sulla colonna di chi lo subisce: palla, lampo, onde, schegge;
 * - `label`   → sulla colonna di chi lo segna: solo «ACE», che cresce e si
 *   allarga (spaziatura fra le lettere) a ogni pulsazione.
 *
 * Tempi (vedi `globals.css`): caduta 0→430 ms, scoppio 430→960 ms, testo da
 * 620 ms per tre pulsazioni da 520 ms → fine a 2,2 s. `ACE_SCENE_MS` chiude la
 * scena a 2,4 s.
 */
import type { CSSProperties } from 'react'

/** Schegge del pavimento: ventaglio deterministico sulla metà alta. */
const SHARDS = 9
/** Palle fantasma della scia: una dietro l'altra, 45 ms l'una. */
const TRAIL = 3

interface Shard {
  dx: string
  dy: string
  rot: string
  size: number
}

function buildShards(): Shard[] {
  return Array.from({ length: SHARDS }, (_, i) => {
    // 196°…344°: il ventaglio sale e si apre ai lati, simmetrico sull'impatto.
    const deg = 196 + (i / (SHARDS - 1)) * 148
    const rad = (deg * Math.PI) / 180
    const dist = 9 + (i % 3) * 4.5
    return {
      dx: `${(Math.cos(rad) * dist).toFixed(2)}cqi`,
      dy: `${(Math.sin(rad) * dist).toFixed(2)}cqi`,
      rot: `${(i % 2 === 0 ? 1 : -1) * (120 + i * 35)}deg`,
      size: 0.45 + (i % 3) * 0.16,
    }
  })
}

const SHARD_GEOMETRY = buildShards()

export function AceScene({
  side,
  accent,
  variant = 'landing',
}: {
  /** Metà su cui si monta (quella che subisce per `landing`, quella che segna per `label`). */
  side: 'home' | 'away'
  /** Colore della squadra di questa metà: onda d'urto e schegge. */
  accent?: string
  /**
   * `landing`: il pallone che cade e scoppia (campo di chi subisce).
   * `label`: la sola scritta «ACE» (campo di chi segna).
   */
  variant?: 'landing' | 'label'
}) {
  // La palla arriva dal lato opposto: sulla colonna di destra viene da sinistra.
  const dir = side === 'away' ? -1 : 1
  const landing = variant === 'landing'

  return (
    <div
      data-ace-scene
      aria-hidden
      className="lv-ace-scene"
      style={{ '--dir': dir, ...(accent ? { '--ace-accent': accent } : {}) } as CSSProperties}
    >
      {landing && (
        <>
      {/* Polvere di fondo campo: si apre sotto l'impatto. */}
      <span className="lv-ace-floor" />

      {/* Scia: le palle fantasma sono la palla di 45/90/135 ms fa. */}
      {Array.from({ length: TRAIL }, (_, i) => (
        <span
          key={`ghost-${i}`}
          className="lv-ace-ghost"
          style={{ '--t': `${(i + 1) * 45}ms` } as CSSProperties}
        />
      ))}

      {/* La palla: bianca, cuciture scure, ruota mentre scende. */}
      <span className="lv-ace-ball">
        <span className="lv-ace-seam lv-ace-seam-a" />
        <span className="lv-ace-seam lv-ace-seam-b" />
        <span className="lv-ace-seam lv-ace-seam-c" />
      </span>

      {/* Scoppio: lampo, due onde d'urto, schegge. */}
      <span className="lv-ace-flash" />
      <span className="lv-ace-ring" />
      <span className="lv-ace-ring lv-ace-ring-b" />
      {SHARD_GEOMETRY.map((shard, i) => (
        <span
          key={`shard-${i}`}
          className="lv-ace-shard"
          style={{
            '--dx': shard.dx,
            '--dy': shard.dy,
            '--rot': shard.rot,
            '--size': shard.size,
          } as CSSProperties}
        />
      ))}
        </>
      )}

      {/* La scritta appartiene alla metà di chi **segna**: nella variante
          `landing` (dove batte la palla) non si monta affatto. */}
      {!landing && <p className="lv-scene-text lv-ace-text">Ace</p>}
    </div>
  )
}

export default AceScene
